/**
 * Step 1 — Intent selection grid.
 *
 * 5 tiles in a 3+2 layout: Suggested Ideas (default, teal) leads, followed
 * by Freeform, Materials, From an Ad, Template.  `requiresPersona` blocks
 * the Suggested Ideas path when no persona is selected.
 */

import { LayoutTemplate, MessageSquare, Paperclip, Sparkles, Zap } from 'lucide-react';
import type { OverlayIntent } from './types';

interface TileDef {
  key: OverlayIntent;
  label: string;
  sublabel: string;
  Icon: typeof Sparkles;
  accent: 'teal' | 'purple' | 'amber' | 'blue' | 'slate';
  requiresPersona?: boolean;
}

const TILES: TileDef[] = [
  {
    key: 'suggested',
    label: 'Ranked Ideas',
    sublabel: 'Start from PDO-grounded suggestions for the persona.',
    Icon: Sparkles,
    accent: 'teal',
    requiresPersona: true,
  },
  {
    key: 'freeform',
    label: 'Describe It',
    sublabel: 'Type the direction and let AI structure it.',
    Icon: MessageSquare,
    accent: 'purple',
  },
  {
    key: 'materials',
    label: 'Source Materials',
    sublabel: 'Upload brand guides, teardowns, ad scripts.',
    Icon: Paperclip,
    accent: 'amber',
  },
  {
    key: 'from_ad',
    label: 'From an Ad',
    sublabel: 'Back-fill the brief from an existing ad creative.',
    Icon: Zap,
    accent: 'blue',
  },
  {
    key: 'template',
    label: 'Template',
    sublabel: 'Start from a standard creative archetype.',
    Icon: LayoutTemplate,
    accent: 'slate',
  },
];

const ACCENT_CLASSES: Record<TileDef['accent'], { ring: string; bg: string; text: string; iconBg: string }> = {
  teal:   { ring: 'hover:border-teal-400 hover:ring-teal-200/40',     bg: 'bg-white',       text: 'text-teal-700',   iconBg: 'bg-teal-50 text-teal-600' },
  purple: { ring: 'hover:border-violet-400 hover:ring-violet-200/40', bg: 'bg-white',       text: 'text-violet-700', iconBg: 'bg-violet-50 text-violet-600' },
  amber:  { ring: 'hover:border-amber-400 hover:ring-amber-200/40',   bg: 'bg-white',       text: 'text-amber-700',  iconBg: 'bg-amber-50 text-amber-600' },
  blue:   { ring: 'hover:border-sky-400 hover:ring-sky-200/40',       bg: 'bg-white',       text: 'text-sky-700',    iconBg: 'bg-sky-50 text-sky-600' },
  slate:  { ring: 'hover:border-slate-400 hover:ring-slate-200/40',   bg: 'bg-white',       text: 'text-slate-700',  iconBg: 'bg-slate-100 text-slate-600' },
};

interface Props {
  hasPersona: boolean;
  onSelect: (intent: OverlayIntent) => void;
}

export function IntentTiles({ hasPersona, onSelect }: Props) {
  return (
    <div className="grid grid-cols-2 lg:grid-cols-3 gap-4">
      {TILES.map((t) => {
        const disabled = !!t.requiresPersona && !hasPersona;
        const accent = ACCENT_CLASSES[t.accent];
        return (
          <button
            key={t.key}
            type="button"
            onClick={() => !disabled && onSelect(t.key)}
            disabled={disabled}
            className={`
              relative flex flex-col items-start text-left p-5 rounded-2xl border border-slate-200
              ${accent.bg} ring-2 ring-transparent transition-all
              ${disabled
                ? 'opacity-50 cursor-not-allowed'
                : `cursor-pointer hover:shadow-md ${accent.ring}`
              }
            `}
          >
            <div className={`w-10 h-10 rounded-xl flex items-center justify-center ${accent.iconBg}`}>
              <t.Icon size={18} />
            </div>
            <h3 className={`mt-3 text-sm font-bold ${accent.text}`}>{t.label}</h3>
            <p className="mt-1 text-[12px] text-slate-500 leading-snug">{t.sublabel}</p>
            {disabled && (
              <span className="mt-2 text-[10px] font-mono uppercase tracking-wider text-slate-400">
                Select a persona to enable
              </span>
            )}
          </button>
        );
      })}
    </div>
  );
}
